import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { ASSET_TEXT_LIMITS, formatMoney, moneyFromCents } from '@wodi/domain';
import type { AssetView, OpenWorkOrderRequest } from '@wodi/shared';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Section,
  Select,
  TextArea,
} from '@wodi/ui';
import { useState } from 'react';

import {
  assetKeys,
  assetQuery,
  changeAssetStatus,
  openWorkOrder,
  updateAsset,
} from '../api/assets.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { dateTimeText, dayText, errorText, LoadFailed, NotAllowed } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { AssetFormDialog } from './asset-form.tsx';
import { WorkOrderTable } from './assets-page.tsx';
import { AssetStatusTag, DueWords } from './words.tsx';

// One asset (docs/plans/phase3-operations-modules.md §3.3): details, status, next service,
// work orders, and the actions the reader may take. Never deleted: condemned is final.

const route = getRouteApi('/_app/assets/$assetId');
const NO_INCIDENT = 'none';

function Facts({ asset }: { asset: AssetView }) {
  return (
    <Section title="Details">
      <dl className={styles.facts}>
        <dt>Status</dt>
        <dd>
          <AssetStatusTag status={asset.status} />
          {asset.statusReason === null ? null : (
            <span className={styles.meta}> {asset.statusReason}</span>
          )}
        </dd>
        <dt>Category</dt>
        <dd>{asset.category.display}</dd>
        <dt>Make and model</dt>
        <dd>{asset.makeModel ?? 'Not recorded'}</dd>
        <dt>Serial number</dt>
        <dd>
          {asset.serialNumber === null ? (
            'Not recorded'
          ) : (
            <span className="wodi-id">{asset.serialNumber}</span>
          )}
        </dd>
        <dt>Location</dt>
        <dd>{asset.location ?? 'Not recorded'}</dd>
        <dt>Department</dt>
        <dd>{asset.department ?? 'Not recorded'}</dd>
        <dt>Supplier</dt>
        <dd>{asset.supplier?.name ?? 'Not recorded'}</dd>
        <dt>Purchased</dt>
        <dd>
          {dayText(asset.purchaseDate)}
          {asset.purchaseCostCents === null
            ? ''
            : `, ${formatMoney(moneyFromCents(BigInt(asset.purchaseCostCents)))}`}
        </dd>
        <dt>Warranty until</dt>
        <dd>{dayText(asset.warrantyUntil)}</dd>
        <dt>Planned service</dt>
        <dd>
          {asset.serviceIntervalDays === null
            ? 'None planned'
            : `Every ${String(asset.serviceIntervalDays)} days`}
          {asset.lastServicedOn === null ? '' : `, last ${dayText(asset.lastServicedOn)}`}
        </dd>
        <dt>Next service</dt>
        <dd>
          <DueWords nextServiceDue={asset.nextServiceDue} dueState={asset.dueState} />
        </dd>
        <dt>Registered</dt>
        <dd>{dateTimeText(asset.registeredAt)}</dd>
      </dl>
    </Section>
  );
}

function OpenWorkOrderDialog({ asset, onClose }: { asset: AssetView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [kind, setKind] = useState<OpenWorkOrderRequest['kind']>('breakdown');
  const [problem, setProblem] = useState('');
  const [incident, setIncident] = useState(NO_INCIDENT);
  const [takeOut, setTakeOut] = useState(true);
  const [tried, setTried] = useState(false);
  const open = useMutation({
    mutationFn: () =>
      openWorkOrder(asset.id, {
        clientRequestId,
        kind,
        problem: problem.trim(),
        incidentId: incident === NO_INCIDENT ? null : incident,
        takeOutOfService: kind === 'breakdown' && takeOut,
      }),
    onSuccess: (wo) => {
      void queryClient.invalidateQueries({ queryKey: assetKeys.all });
      toasts.show({ title: `Work order opened, ${wo.workOrderNumber}` });
      void navigate({ to: '/work-orders/$workOrderId', params: { workOrderId: wo.id } });
    },
  });
  return (
    <Dialog
      title="Open work order"
      isOpen
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <div className={styles.form}>
        <RadioGroup
          label="Kind"
          value={kind}
          onChange={(v) => {
            setKind(v as OpenWorkOrderRequest['kind']);
          }}
          options={[
            { id: 'breakdown', label: 'Breakdown' },
            { id: 'planned', label: 'Planned maintenance' },
          ]}
        />
        <TextArea
          label={kind === 'breakdown' ? 'What is wrong' : 'What is to be done'}
          value={problem}
          onChange={setProblem}
          maxLength={ASSET_TEXT_LIMITS.problem}
          {...(tried && problem.trim() === '' ? { errorMessage: 'Describe the work.' } : {})}
        />
        {asset.incidents.length === 0 ? null : (
          <Select
            label="Linked incident"
            options={[
              { id: NO_INCIDENT, label: 'None' },
              ...asset.incidents.map((i) => ({
                id: i.id,
                label: `${i.incidentNumber}, ${dateTimeText(i.occurredAt)}`,
              })),
            ]}
            value={incident}
            onChange={(key) => {
              setIncident(key === null ? NO_INCIDENT : String(key));
            }}
          />
        )}
        {kind === 'breakdown' && asset.status === 'in_service' ? (
          <Checkbox isSelected={takeOut} onChange={setTakeOut}>
            Take the asset out of service
          </Checkbox>
        ) : null}
        {open.isError ? (
          <Notice tone="error">{errorText(open.error, "It wasn't opened. Try again.")}</Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Go back
        </Button>
        <Button
          variant="primary"
          isPending={open.isPending}
          pendingLabel="Opening…"
          onPress={() => {
            setTried(true);
            if (problem.trim() !== '') open.mutate();
          }}
        >
          Open work order
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type Dialogs = 'edit' | 'work' | 'out' | 'in' | 'condemn' | null;

export function AssetPage() {
  const { assetId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const allowed = session !== null && can(session, 'assets.read');
  const asset = useQuery({ ...assetQuery(assetId), enabled: allowed });
  const [dialog, setDialog] = useState<Dialogs>(null);
  const done = (title: string) => (view: AssetView) => {
    queryClient.setQueryData(assetKeys.one(view.id), view);
    void queryClient.invalidateQueries({ queryKey: ['assets', 'list'] });
    toasts.show({ title });
    setDialog(null);
  };
  const edit = useMutation({
    mutationFn: (body: Parameters<typeof updateAsset>[1]) => updateAsset(assetId, body),
    onSuccess: done('Asset details saved'),
  });
  const status = useMutation({
    mutationFn: (body: Parameters<typeof changeAssetStatus>[1]) => changeAssetStatus(assetId, body),
    onSuccess: (view) => {
      done(
        view.status === 'condemned'
          ? 'Asset condemned'
          : view.status === 'in_service'
            ? 'Asset back in service'
            : 'Asset out of service',
      )(view);
    },
  });
  if (!allowed) return <NotAllowed title="Asset" what="see the asset register" />;
  const a = asset.data;
  const manage = can(session, 'assets.manage');
  const maintain = can(session, 'assets.maintain');
  return (
    <div className={styles.page}>
      <PageHeader title={a === undefined ? 'Asset' : `${a.tag}, ${a.name}`} />
      <p className={styles.meta}>
        <Link to="/assets">Assets</Link>
      </p>
      {online ? null : (
        <Notice tone="info">
          You&apos;re offline. Changes work again when you&apos;re back online.
        </Notice>
      )}
      {asset.isPending ? <LoadingBlock label="Loading the asset" rows={8} /> : null}
      {asset.isError ? (
        <LoadFailed
          error={asset.error}
          fallback="The asset couldn't be loaded. Try again."
          onRetry={() => void asset.refetch()}
        />
      ) : null}
      {a === undefined ? null : (
        <>
          {a.status === 'condemned' ? null : (
            <div className={styles.actions}>
              {maintain ? (
                <Button
                  variant="primary"
                  isDisabled={!online}
                  onPress={() => {
                    setDialog('work');
                  }}
                >
                  Open work order
                </Button>
              ) : null}
              {manage ? (
                <>
                  <Button
                    isDisabled={!online}
                    onPress={() => {
                      edit.reset();
                      setDialog('edit');
                    }}
                  >
                    Change details
                  </Button>
                  {a.status === 'in_service' ? (
                    <Button
                      isDisabled={!online}
                      onPress={() => {
                        status.reset();
                        setDialog('out');
                      }}
                    >
                      Take out of service
                    </Button>
                  ) : (
                    <Button
                      isDisabled={!online}
                      isPending={status.isPending}
                      pendingLabel="Saving…"
                      onPress={() => {
                        status.mutate({ status: 'in_service', reason: null });
                      }}
                    >
                      Put back in service
                    </Button>
                  )}
                  <Button
                    variant="quiet"
                    isDisabled={!online}
                    onPress={() => {
                      status.reset();
                      setDialog('condemn');
                    }}
                  >
                    Condemn
                  </Button>
                </>
              ) : null}
            </div>
          )}
          {status.isError && dialog === null ? (
            <Notice tone="error">
              {errorText(status.error, "The status wasn't changed. Try again.")}
            </Notice>
          ) : null}
          <Facts asset={a} />
          <Section title="Work orders" description="The latest first.">
            <WorkOrderTable items={a.workOrders} empty="No work orders yet." />
          </Section>
          {dialog === 'edit' ? (
            <AssetFormDialog
              title="Change details"
              confirmLabel="Save details"
              asset={a}
              isPending={edit.isPending}
              error={edit.error}
              onSubmit={(body) => {
                // The tag is fixed once registered; the server never reads it here.
                edit.mutate({ ...body, tag: undefined } as Parameters<typeof updateAsset>[1]);
              }}
              onClose={() => {
                setDialog(null);
              }}
            />
          ) : null}
          {dialog === 'work' ? (
            <OpenWorkOrderDialog
              asset={a}
              onClose={() => {
                setDialog(null);
              }}
            />
          ) : null}
          {dialog === 'out' || dialog === 'condemn' ? (
            <ReasonDialog
              title={dialog === 'out' ? 'Take out of service' : 'Condemn the asset'}
              isOpen
              onOpenChange={(o) => {
                if (!o) setDialog(null);
              }}
              confirmLabel={dialog === 'out' ? 'Take out of service' : 'Condemn'}
              pendingLabel="Saving…"
              isPending={status.isPending}
              error={
                status.isError ? errorText(status.error, 'That didn’t work. Try again.') : null
              }
              onConfirm={(reason) => {
                status.mutate({
                  status: dialog === 'out' ? 'out_of_service' : 'condemned',
                  reason,
                });
              }}
            >
              {dialog === 'condemn' ? (
                <p className={styles.prose}>
                  A condemned asset stays in the register and can&apos;t be changed again.
                </p>
              ) : null}
            </ReasonDialog>
          ) : null}
        </>
      )}
    </div>
  );
}
